import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import {
  ApiError,
  AuthService,
  ButtonLoader,
  ConfigService,
  FormsUtilsService,
  MessageService,
  notCloseMessageOptions,
} from '../../../../../core';
import {
  OrigemLancamentoCadastro,
  origemLancamentoCadastroProspeccaoOutbound,
  origemLancamentoCadastroSogivendas,
} from '../../../../../models/origem-lancamento-cadastro';
import { ConfigEmpresa } from '../../../../configuracoes/app/empresa';
import { contatoTipoPrincipal } from '../../../../pessoa/contato';
import { enderecoTipoPrincipal } from '../../../../pessoa/endereco';
import {
  isTipoPessoaCentralCompras,
  isTipoPessoaFisica,
  isTipoPessoaJuridica,
  pessoaFisica,
  pessoaJuridica,
} from '../../../../pessoa/tipo';
import { Usuario } from '../../../../usuario';
import { LeadClassificacaoFormComponent } from '../../classificacao/form';
import { LeadContatoComponent, LeadContatoUnicoComponent } from '../../contato';
import { LeadEnderecoUnicoComponent } from '../../endereco';
import { Lead } from '../../lead';
import { ConfigCliente } from '../../../../configuracoes/app/cliente';
import { LeadValidacoesHttpService } from '../../validacoes';
import { ClienteValidacoesHttpService } from '../../../../pessoa/cliente/validacoes';
import { Origem, OrigemID } from '../../../../common/origens/domain';
import { OrigemSelectComponent } from '../../../../common/origens/components/select';
import {
  CampanhaSelect,
  mapToCampanha,
} from '../../../../campanhas/campanhas/components/select/campanha-select';
import { CampanhaSelectComponent } from '../../../../campanhas/campanhas/components/select';
import { CanalVendaSelectComponent } from '../../../../common/canais-venda/components/select';
import { CanalMidiaSocialSelectComponent } from '../../../../common/canal-midia-social/components/select';
import { take } from 'rxjs/operators';
import { SimplesNacionalFormComponent } from '../../../../common/simples-nacional';
import { MEIFormComponent } from '../../../../common/mei';
import { LeadIntegracaoEmpresasImportHttpService } from '../../integracoes/empresas/services';
import { LeadCrudHttpService, LeadAuthGuardService } from '../../services';
import { LeadPreInsert, LeadPreInsertFormComponent } from './pre-insert';
import { RepresentanteClassificacaoClienteHttpService } from '../../../../pessoa/representante/classificacao-cliente';
import { ISelectSettings } from 'app/domains/shared/components/select/models/select-settings.interface';
import { ToastService } from 'app/domains/shared/components/toast/services/toast.service';

interface Config {
  canEditOrigem: boolean;
  podeCadastrarLeadExterior: boolean;
  isOrigemPossuiCanalVenda?: boolean;
}

@Component({
  selector: 'sgt-lead-form',
  templateUrl: './lead-form.component.html',
  styleUrls: ['./lead-form.component.less'],
})
export class LeadFormComponent implements OnInit, OnDestroy {
  config: Config;
  configEmpresa: ConfigEmpresa;
  configCliente: ConfigCliente;
  isRepresentante: boolean;
  isShowPreInsertForm = false;
  preInsertNome: string;
  isConsultandoCnpj = false;

  @Input() entity: Lead;
  @Output() entityChange = new EventEmitter<Lead>();
  @Input() editMode: boolean;
  @Input() isLeadExterior = false; // Se for um contato de um lead do exterior
  @Input() idRepresentante: number;

  /** Options */
  @Input() isShowOrigem = true;

  @Output() cancel = new EventEmitter<void>();

  pessoaTipos = [pessoaFisica, pessoaJuridica];

  @ViewChild('form') form: NgForm;
  @ViewChild('preInsertForm') preInsertForm: LeadPreInsertFormComponent;
  @ViewChild('origemSelect') origemSelect: OrigemSelectComponent;
  @ViewChild('nomeInput') nomeInput: ElementRef;
  @ViewChild('cnpjCpfInput') cnpjCpfInput: ElementRef;
  @ViewChild('simplesNacionalForm')
  simplesNacionalForm: SimplesNacionalFormComponent;
  @ViewChild('meiForm') meiForm: MEIFormComponent;

  // Campanhas
  @ViewChild('campanhaOrigemSelect')
  campanhaOrigemSelect: CampanhaSelectComponent;
  // Canal de Venda de Origem
  @ViewChild('canalVendaOrigemSelect')
  canalVendaOrigemSelect: CanalVendaSelectComponent;
  // Mídias Sociais
  @ViewChild('canalMidiaSocialSelect')
  canalMidiaSocialSelect: CanalMidiaSocialSelectComponent;

  // Classificaçõdes
  @ViewChild('leadClassificacaoForm')
  leadClassificacaoForm: LeadClassificacaoFormComponent;

  /** Se o form deve ser exibido em tamanho pequeno */
  @Input() isMini = false;
  /** Se deve exibir o usuário responsável */
  @Input() isShowUsuarioResponsavel = true;
  /** Se deve exibir a temperatura */
  @Input() isShowTemperatura = true;

  // Contatos
  tipoContatoPrincipal = contatoTipoPrincipal.id;
  @ViewChild('formContatoPrincipal')
  formContatoPrincipal: LeadContatoUnicoComponent;
  @ViewChild('leadOutrosContatosCadastro')
  leadOutrosContatosCadastro: LeadContatoComponent;

  // Endereços
  tipoEnderecoPrincipal = enderecoTipoPrincipal.id;
  @ViewChild('formEnderecoPrincipal')
  formEnderecoPrincipal: LeadEnderecoUnicoComponent;

  // Permissões do usuários
  usuarioPodeSalvarCnpjCpfInvalido: boolean; // Se o usuário pode salvar o CNPJ/CPF inválido
  ignorarCnpjCpfInvalido = false; // Se deve ignorar o erro de CNPJ/CPF inválido

  selectPeriodicidadeCompraSettings: ISelectSettings = {
    placeholder: 'Selecione uma periodicidade de compra',
    isDisabled: false,
    required: true,
    requiredFieldMessage: 'A periodicidade de compra é obrigatória',
    isClearSelectionAllowed: false,
  };

  constructor(
    private authService: AuthService,
    private configService: ConfigService,
    private formsUtilsService: FormsUtilsService,
    private messageService: MessageService,
    private leadValidacoesHttpService: LeadValidacoesHttpService,
    private leadAuthGuardService: LeadAuthGuardService,
    private leadCrudHttpService: LeadCrudHttpService,
    private clienteValidacoesHttpService: ClienteValidacoesHttpService,
    private leadIntegracaoEmpresasImportHttpService: LeadIntegracaoEmpresasImportHttpService,
    private representanteClassificacaoClienteHttpService: RepresentanteClassificacaoClienteHttpService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.configEmpresa = this.configService.getEmpresa();
    this.configCliente = this.configService.getCliente();
    this.isRepresentante = this.authService.isRepresentante();

    // Permissões do usuários
    this.usuarioPodeSalvarCnpjCpfInvalido =
      this.authService.podeSalvarCnpjCpfInvalido();
  }

  private setConfig() {
    this.config = {
      canEditOrigem: this.leadAuthGuardService.canEditOrigem(
        this.entity,
        this.editMode
      ),
      podeCadastrarLeadExterior:
        this.configEmpresa?.exportadora &&
        this.authService.podeCadastrarClienteExterior(),
    };
    this.setConfigByOrigem();
  }

  /** Seta as configurações do form conforme a origem do cliente */
  private setConfigByOrigem() {
    if (!this.entity) {
      return;
    }
    // Obtendo a origem do cliente
    const origem = Origem.getById(this.entity.origem);

    // Somente exibe o canal de venda se a origem possuir
    this.config.isOrigemPossuiCanalVenda = origem?.possuiCanalVenda ?? false;
  }

  load(id: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.leadCrudHttpService.load(id).subscribe(
        (entity) => {
          // Se não puder editar o lead
          if (this.editMode && !this.leadAuthGuardService.canEdit(entity)) {
            this.messageService.info(
              'Você não possui permissão para acessar este cadastro de lead. Verique seus acessos ou entre em contato com o suporte',
              'Sem permissão'
            );
            reject();
            return;
          }

          this.entity = entity;

          resolve();
        },
        (error: ApiError) => {
          this.messageService.error(
            error.message,
            'Erro ao carregar o Lead',
            error
          );
          reject();
        }
      );
    });
  }

  /** Executa a chamada da confirmação do pré-insert */
  execConfirmPreInsert() {
    this.preInsertForm.confirmClick();
  }

  /** Abre o pré-insert do cadastro de um novo lead */
  preInsert(nome: string = null) {
    this.isShowPreInsertForm = true;
    this.preInsertNome = nome;
    this.editMode = false;
  }

  /** Ao confirmar o pré-insert de um novo lead */
  onConfirmPreInsert(leadPreInsert: LeadPreInsert) {
    // Pessoa Jurídica encontrada
    if (
      leadPreInsert.isTipoPessoaJuridica() &&
      leadPreInsert.leadPessoaJuridica
    ) {
      this.editMode = false;
      this.isShowPreInsertForm = false;
      this.entity = leadPreInsert.leadPessoaJuridica;
      this.entity.idEmpresa = this.authService.getIdEmpresa();
      this.entity.exterior = leadPreInsert.exterior;
      this.afterInsert();
    } else {
      // Demais
      this.insert(
        origemLancamentoCadastroSogivendas,
        leadPreInsert.nome,
        leadPreInsert.tipoPessoa,
        leadPreInsert.cnpjCpf,
        leadPreInsert.exterior
      );
    }

    this.entity.origem = leadPreInsert.origem;
    this.entity.idCampanhaOrigem = leadPreInsert.idCampanhaOrigem;
    this.entity.campanhaOrigem = leadPreInsert.campanhaOrigem;
    this.entity.idCanalVendaOrigem = leadPreInsert.idCanalVendaOrigem;
    this.entity.canalVendaOrigem = leadPreInsert.canalVendaOrigem;
    this.entity.canalMidiaSocial = leadPreInsert.canalMidiaSocial;
    this.entity.idUsuarioResponsavel = leadPreInsert.idUsuarioResponsavel;
    this.entity.usuarioResponsavel = leadPreInsert.usuarioResponsavel;
    this.entity.temperatura = leadPreInsert.temperatura;

    setTimeout(() => {
      this.setConfigByOrigem();
      this.setFocusNome();
    }, 300);
  }

  insert(
    origemCadastro: OrigemLancamentoCadastro,
    nomeLead: string = null,
    tipoPessoaDefault: number = null,
    cnpjCpf: string = null,
    exterior = false
  ) {
    this.editMode = false;
    this.isShowPreInsertForm = false;
    this.entity = new Lead(origemCadastro, null, tipoPessoaDefault);
    this.entity.idEmpresa = this.authService.getIdEmpresa();
    this.entity.nome = nomeLead;
    this.entity.exterior = exterior;

    if (cnpjCpf) {
      this.entity.cnpjCpf = cnpjCpf;
      this.onValidarCnpjCpfCadastrado();
    }

    // Setando o usuário logado como o lead sendo inserido
    this.setUsuarioLogadoComoResponsavel();

    // Classificações
    setTimeout(() => {
      this.insertClassificacoes();
    }, 0);

    setTimeout(() => {
      // Setando a cidade e país no endereço principal
      this.insertEnderecoPrincipal();
    }, 300);

    this.afterInsert();
    this.setFocus();
  }

  private afterInsert() {
    // Se for representante...,
    if (this.isRepresentante) {
      // Setando as classificações através das classificações padrões do representante
      this.setClassificacoesByRepresentante();
    }

    this.setConfig();
  }

  /** Seta o usuário logado como o responsável pelo lead */
  private setUsuarioLogadoComoResponsavel() {
    const usuarioResponsavel = this.authService.getUsuario();
    if (usuarioResponsavel) {
      this.entity.idUsuarioResponsavel = usuarioResponsavel.id;
      this.entity.usuarioResponsavel = usuarioResponsavel;
    }
  }

  /** Insere um novo lead a partir da Prospecção Outbound */
  insertByProspeccaoOutbound(cnpj: string) {
    const origemCadastro = origemLancamentoCadastroProspeccaoOutbound;
    // Executando o insert normal
    this.insert(origemCadastro);
    setTimeout(async () => {
      // Consultando o CNPJ
      await this.onConsultarCnpj(origemCadastro, cnpj);

      // Setando origem do lead como Prospecção Ativa
      this.entity.origem = Origem.ORIGEM_PROSPECCAO_ATIVA.id;
    }, 0);
  }

  async edit(id: number) {
    try {
      this.editMode = true;
      await this.load(id);

      this.setConfig();
      this.setFocus();
    } catch (error) {
      this.cancel.emit();
    }
  }

  /** Seta foco conforme a origem */
  setFocus() {
    setTimeout(() => {
      if (!this.entity.origem) {
        this.setFocusOrigem();
      } else {
        if (this.config.isOrigemPossuiCanalVenda) {
          this.setFocusCanalVendaOrigem();
        } else if (this.isOrigemCampanha()) {
          this.setFocusCampanhaOrigem();
        } else if (this.isOrigemMidiaSocial()) {
          this.setFocusCanalMidiaSocial();
        } else {
          this.setFocusNome();
        }
      }
    });
  }

  /** Seta foco no select da origem */
  private setFocusOrigem() {
    if (!this.config.canEditOrigem) {
      return;
    }
    this.origemSelect?.setFocus();
  }

  /** Seta foco no select da campanha de origem */
  private setFocusCampanhaOrigem() {
    this.campanhaOrigemSelect?.setFocus();
  }

  /** Seta foco no select do canal de venda de origem */
  private setFocusCanalVendaOrigem() {
    this.canalVendaOrigemSelect?.setFocus();
  }

  /** Seta foco no select do canal da mídia social */
  private setFocusCanalMidiaSocial() {
    this.canalMidiaSocialSelect?.setFocus();
  }

  /** Seta foco no input do CNPJ/CPF */
  private setFocusCnpjCPF() {
    jQuery(this.cnpjCpfInput?.nativeElement).trigger('focus');
  }

  /** Seta foco no input do nome */
  private setFocusNome() {
    jQuery(this.nomeInput?.nativeElement).trigger('focus');
  }

  /** Ao alterar a origem */
  onChangeOrigem(origem: number) {
    this.entity.origem = origem;
    this.setConfigByOrigem();
    this.setFocus();
  }

  onChangePeriodicidadeCompra(idPeriodicidadeCompra: number) {
    this.entity.idPeriodicidadeCompra = idPeriodicidadeCompra;
  }

  /** Verifica se a origem do lead é campanha */
  isOrigemCampanha(): boolean {
    if (!this.entity) {
      return false;
    }
    return OrigemID.isOrigemCampanha(this.entity.origem);
  }

  /** Verifica se a origem do lead é mídia social */
  isOrigemMidiaSocial(): boolean {
    if (!this.entity) {
      return false;
    }
    return OrigemID.isOrigemMidiaSocial(this.entity.origem);
  }

  /** Ao alterar a campanha de origem do lead */
  onChangeCampanhaOrigem(campanha: CampanhaSelect) {
    this.entity.campanhaOrigem = mapToCampanha(campanha);
  }

  /** Verifica se o tipo da pessoa é Fisica */
  isTipoPessoaFisica(): boolean {
    return isTipoPessoaFisica(this.entity?.tipoPessoa);
  }

  /** Verifica se o tipo da pessoa é Juridica */
  isTipoPessoaJuridica(): boolean {
    return isTipoPessoaJuridica(this.entity?.tipoPessoa);
  }

  /** Verifica se o tipo da pessoa é Central de Compras */
  isTipoPessoaCentralCompras(): boolean {
    return isTipoPessoaCentralCompras(this.entity?.tipoPessoa);
  }

  /** Obtem o label do CNPJ/CPF pelo tipo da pessoa selecionado */
  getLabelCnpjCpfByTipo() {
    // Sem tipo
    if (!this.entity || !this.entity.tipoPessoa) {
      return 'CNPJ / CPF';
    }
    // Física
    if (this.isTipoPessoaFisica()) {
      return 'CPF';
    }
    // Júridica
    if (this.isTipoPessoaJuridica()) {
      return 'CNPJ';
    }

    // Padrão
    return 'CNPJ / CPF';
  }

  /** Verifica se o CNPJ/CPF é obrigatório */
  isCnpjCpfRequired(): boolean {
    if (!this.entity) {
      return false;
    }

    // Se a origem for campanha e a campanha permitir cadastrar leads sem CNPJ/CPF, então não é obrigatório
    if (
      this.isOrigemCampanha() &&
      this.entity.campanhaOrigem &&
      this.entity.campanhaOrigem.permitirCadastrarLeadSemCnpjCpf
    ) {
      return false;
    }

    // Somente quando for uma pessoa do tipo Física ou Juridica que não seja do exterior
    return this.isTipoPessoaJuridica() && !this.entity.exterior;
  }

  /** Verifica se deve validar o CNPJ/CPF */
  mustValidateCnpjCpf(): boolean {
    if (!this.entity) {
      return true;
    }

    // Se o usuário poder salvar com o CNPJ/CPF e estiver selecionado para ignorar o erro de validação, então não deve validar
    if (this.usuarioPodeSalvarCnpjCpfInvalido && this.ignorarCnpjCpfInvalido) {
      return false;
    }

    // Se for do exterior, então não deve validar
    if (this.isLeadExterior) {
      return false;
    }

    return true;
  }

  onConsultarCnpj(
    origemCadastro: OrigemLancamentoCadastro,
    cnpj: string
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (this.editMode) {
        this.messageService.warning(
          'Não é possível consultar o CNPJ de um lead já cadastrado',
          'Atenção',
          notCloseMessageOptions
        );
        return false;
      }

      if (!cnpj || cnpj === '') {
        this.messageService.warning(
          'Informe um CNPJ válido para poder consultá-lo',
          'Atenção'
        );
        this.setFocusCnpjCPF();
        reject();
        return;
      }

      this.isConsultandoCnpj = true;
      this.leadIntegracaoEmpresasImportHttpService
        .consultaCNPJ(cnpj)
        .pipe(take(1))
        .subscribe(
          (response) => {
            this.entity = response;
            // Setando a origem do cadastro após a consulta para alterar para a origem desejada
            if (origemCadastro) {
              this.entity.origemCadastro = origemCadastro.id;
            }

            // Se estiver inserindo
            if (!this.editMode) {
              // Setando o usuário logado como o lead sendo inserido
              this.setUsuarioLogadoComoResponsavel();
            }

            this.entityChange.emit(this.entity);
            this.isConsultandoCnpj = false;
            resolve();
          },
          (error: ApiError) => {
            if (error.status == 404) {
              // Nenhum CNPJ encontrado na base de dados
              this.messageService.warning(
                error.message,
                error.cause,
                notCloseMessageOptions
              );
            } else {
              this.messageService.error(error.message, error.cause, error);
            }

            this.isConsultandoCnpj = false;
            reject();
          }
        );
    });
  }

  /** Valida se já existe um lead cadastrado com o mesmo CNPJ do lead informado */
  private validadeLeadJaCadastro(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!this.entity) {
        resolve();
        return;
      }
      // Se for um lead do exterior, então ignoramos a validação pois o mesmo não possui um CNPJ
      if (this.entity.exterior) {
        resolve();
        return;
      }
      // Se for uma central de compras não precisa validar
      if (this.isTipoPessoaCentralCompras()) {
        resolve();
        return;
      }

      this.leadValidacoesHttpService
        .getLeadJaCadastrado(this.entity.id, this.entity.cnpjCpf, this.editMode)
        .subscribe(
          (validacao) => {
            // 1 - Verificando se o CNPJ Matriz (parte até a "/") já esta cadastrado
            if (validacao.cnpjMatrizJaCadastrado) {
              reject(
                'Não é possível cadastrar o lead por que já existe um outro lead cadastrado com o CNPJ da Matriz igual ao informado. Por favor verifique o cadastro de leads ou entre em contato com o comercial para mais informações'
              );
              this.cancel.emit();
              return;
            }

            // 2 - Verificando se o lead já está cadastrado para o representante ou para a empresa
            if (validacao.jaCadastrado) {
              reject(
                'Já existe um lead cadastrado com este CNPJ/CPF informado. Por favor verifique o cadastro de leads ou entre em contato com o comercial para mais informações'
              );
              this.cancel.emit();
              return;
            }

            resolve();
          },
          (error: ApiError) =>
            this.messageService.error(
              error.message,
              'Ocorreu um erro ao verificar se o já existe um lead com o CNPJ/CPF cadastrado',
              error
            )
        );
    });
  }

  /** Valida se já existe um prospect ou cliente cadastrado com o mesmo CNPJ do lead informado */
  private validadeClienteJaCadastro(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!this.entity) {
        resolve();
        return;
      }
      // Se for um lead do exterior, então ignoramos a validação pois o mesmo não possui um CNPJ
      if (this.entity.exterior) {
        resolve();
        return;
      }
      // Se for uma central de compras não precisa validar
      if (this.isTipoPessoaCentralCompras()) {
        resolve();
        return;
      }

      this.clienteValidacoesHttpService
        .getClienteJaCadastrado(null, this.entity.cnpjCpf, null, this.editMode)
        .subscribe(
          (validacao) => {
            // 1 - Verificando se o CNPJ Matriz (parte até a "/") já esta cadastrado
            if (validacao.cnpjMatrizJaCadastrado) {
              reject(
                'Não é possível cadastrar o lead por que já existe um prospect ou cliente cadastrado com o CNPJ da Matriz igual ao informado. Por favor verifique o cadastro de prospect e clientes ou entre em contato com o comercial para mais informações'
              );
              this.cancel.emit();
              return;
            }

            // 2 - Verificando se o cliente já está cadastrado para o representante ou para a empresa
            if (validacao.jaCadastrado) {
              reject(
                'Já existe um prospect ou cliente cadastrado com este CNPJ/CPF informado. Por favor verifique o cadastro de prospect e clientes ou entre em contato com o comercial para mais informações'
              );
              this.cancel.emit();
              return;
            }

            // 3 - Sem permissão para cadastrar clientes secundários
            if (validacao.clienteSecundarioNaoPermitido) {
              reject(
                'O CNPJ/CPF informado já está cadastrado para outro prospect ou cliente e não é permitido cadastrar clientes secundários. Por favor verifique o cadastro de prospect e clientes ou entre em contato com o comercial para mais informações'
              );
              this.cancel.emit();
              return;
            }

            // 4 - Verificando se o cliente já está cadastrado como cliente primario para outro representante ou para a empresa
            if (validacao.clientePrimarioJaCadastrado) {
              reject(
                'Já existe um prospect ou cliente cadastrado com este CNPJ/CPF informado mas que não pertence a sua carteira de clientes. Por favor verifique o cadastro de prospect e clientes ou entre em contato com o comercial para mais informações'
              );
              this.cancel.emit();
              return;
            }

            // 5 - Verificando se a pessoa já está cadastrada
            if (validacao.pessoaJaCadastrada) {
              reject(
                'Já existe um prospect ou cliente cadastrado com este CNPJ/CPF informado. Por favor verifique o cadastro de prospect e clientes ou entre em contato com o comercial para mais informações'
              );
              this.cancel.emit();
              return;
            }

            resolve();
          },
          (error: ApiError) =>
            this.messageService.error(
              error.message,
              'Ocorreu um erro ao verificar se o já existe um prospect ou cliente com o CNPJ/CPF cadastrado',
              error
            )
        );
    });
  }

  /** Realiza a validação de cadastro já existe do cliente e pessoa através do CNPJ/CPF informado */
  async onValidarCnpjCpfCadastrado() {
    try {
      if (!this.entity || !this.entity.cnpjCpf) {
        return;
      }

      // Validando lead
      await this.validadeLeadJaCadastro();
      // Validando prospect e cliente
      if (!this.editMode) {
        await this.validadeClienteJaCadastro();
      }
    } catch (error) {
      this.messageService.info(error, 'Informação', notCloseMessageOptions);
    }
  }

  /** Ao alterar o usuário responsável pelo lead */
  onChangeUsuarioResponsavel(usuarioResponsavel: Usuario) {
    if (usuarioResponsavel) {
      this.entity.idUsuarioResponsavel = usuarioResponsavel.id;
    } else {
      this.entity.idUsuarioResponsavel = null;
    }
  }

  /** Insere as classificações no lead */
  insertClassificacoes() {
    this.leadClassificacaoForm
      ?.insert()
      .then(() => {})
      .catch(() => {});
  }

  /** Seta as classificações através das classificações padrões do representante */
  private setClassificacoesByRepresentante() {
    // Se não estiver inserindo um novo cliente e se não for um representante
    if (this.editMode || !this.isRepresentante) {
      return;
    }

    // Pesquisando as classificações de cliente do representante selecionado
    this.representanteClassificacaoClienteHttpService
      .search(this.authService.getIdRepresentante())
      .pipe(take(1))
      .subscribe(
        (classificaoesClientes) => {
          // Percorrendo as classificações de clientes do representante e setando a classificação equivalente ao tipo de classificação
          classificaoesClientes?.forEach((classificacaoCliente) => {
            for (let i = 0; i < this.entity.classificacoes.length; i++) {
              const entityClassificacao = this.entity.classificacoes[i];

              // Setamos quando for o mesmo tipo de classificação
              if (
                entityClassificacao.idTipoClassificacao ===
                classificacaoCliente.idTipoClassificacao
              ) {
                entityClassificacao.idClassificacao =
                  classificacaoCliente.idClassificacao;
                entityClassificacao.classificacao =
                  classificacaoCliente.classificacao;
                break;
              }
            }
          });
        },
        (error: ApiError) => {
          if (this.isRepresentante) {
            this.messageService.error(
              error.message,
              'Ocorreu um erro ao carregar os seus segmentos de clientes padrões. Tente novamente ou informe manualmente os segmentos no cliente',
              error
            );
          } else {
            this.messageService.error(
              error.message,
              'Ocorreu um erro ao carregar os segmentos de clientes padrões do representante selecionado. Tente novamente ou informe manualmente os segmentos no cliente',
              error
            );
          }
        }
      );
  }

  /** Insere o endereço principal no lead */
  insertEnderecoPrincipal() {
    this.formEnderecoPrincipal?.setCidadeEPais();
  }

  isValid(): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      // Pré-insert
      if (this.isShowPreInsertForm) {
        const isValid = await this.preInsertForm.isValid();
        resolve(isValid);
        return;
      }

      this.formsUtilsService.makeAllFieldsDirty(this.form);

      // Se puder editar a origem, então validamos a origem
      if (this.config.canEditOrigem) {
        // Origem não selecionada
        if (!this.origemSelect.isValid) {
          this.setFocusOrigem();
          this.messageService.info(
            'Selecione a origem deste lead e tente novamente',
            'Origem do lead não selecionada'
          );
          resolve(false);
          return;
        }

        // Canal de venda de origem
        if (
          this.config.isOrigemPossuiCanalVenda &&
          !this.canalVendaOrigemSelect?.isValid
        ) {
          this.messageService.warning(
            'Selecione o canal de venda de origem deste lead e tente novamente',
            'Canal de venda de origem não selecionado',
            notCloseMessageOptions
          );
          setTimeout(() => this.setFocusCanalVendaOrigem(), 300);
          resolve(false);
          return;
        }

        // Canal da mídia social
        if (
          this.isOrigemMidiaSocial() &&
          !this.canalMidiaSocialSelect.isValid
        ) {
          this.setFocusCanalMidiaSocial();
          this.messageService.info(
            'Selecione o canal da mídia social que gerou este lead e tente novamente',
            'Canal da mídia social não selecionado'
          );
          resolve(false);
          return;
        }
      }

      // Pessoa Física
      if (this.isTipoPessoaFisica()) {
        if (this.isCnpjCpfRequired() && !this.entity.cnpjCpf) {
          this.messageService.info(
            'Informe o CPF do lead e tente novamente',
            'CPF não informado'
          );
          this.setFocusCnpjCPF();
          resolve(false);
          return;
        }
      }

      // Pessoa Jurídica
      if (this.isTipoPessoaJuridica()) {
        if (this.isCnpjCpfRequired() && !this.entity.cnpjCpf) {
          this.messageService.info(
            'Informe o CNPJ do lead e tente novamente',
            'CNPJ não informado'
          );
          this.setFocusCnpjCPF();
          resolve(false);
          return;
        }
      }

      // Classificações
      if (this.leadClassificacaoForm && !this.leadClassificacaoForm.isValid()) {
        // this.messageService.warning('Verifique as informações dos segmentos do Lead e tente novamente', 'Os segmentos do Lead não são válidos');
        resolve(false);
        return;
      }
      // Contato
      if (!this.formContatoPrincipal.isValid()) {
        this.messageService.warning(
          'Verifique as informações do contato do Lead e tente novamente',
          'O contato do Lead não é válido'
        );
        resolve(false);
        return;
      }
      // Outros contatos
      if (this.leadOutrosContatosCadastro?.isFormOpen) {
        this.messageService.info(
          'Salve ou cancele o contato para poder salvar o lead',
          'Informação'
        );
        this.leadOutrosContatosCadastro.setFocusForm();
        resolve(false);
        return;
      }

      // Endereço
      if (!this.formEnderecoPrincipal.isValid()) {
        this.messageService.warning(
          'Verifique as informações do endereço do Lead e tente novamente',
          'O endereço do Lead não é válido'
        );
        resolve(false);
        return;
      }

      // Simples Nacional
      if (this.simplesNacionalForm && !this.simplesNacionalForm.isValid()) {
        resolve(false);
        return;
      }

      // MEI
      if (this.meiForm && !this.meiForm.isValid()) {
        resolve(false);
        return;
      }

      if (!this.form.valid) {
        this.formsUtilsService.showMsgFormInvalid();
        resolve(false);
        return;
      }

      // Periodicidade de Compra
      if (!this.entity.idPeriodicidadeCompra) {
        this.toastService.warning(
          'Por favor, informe a periodicidade de compra',
          { duration: 3000 }
        );
        resolve(false);
        return;
      }

      resolve(true);
    });
  }

  save(): Promise<Lead> {
    return new Promise<Lead>(async (resolve, reject) => {
      const isValid = await this.isValid();
      if (!isValid) {
        reject();
        return;
      }

      this.leadCrudHttpService.save(this.entity).subscribe(
        (entityResponse) => {
          this.messageService.success('', 'Lead salvo com sucesso');
          this.entity = entityResponse;
          resolve(entityResponse);
        },
        (error: ApiError) => {
          this.messageService.error(error.message, error.cause, error);
          reject();
        }
      );
    });
  }

  clear() {
    this.entity = null;
    this.isShowPreInsertForm = false;
    this.preInsertNome = null;
  }

  ngOnDestroy(): void {
    this.clear();
  }
}
